<script setup>
import { computed, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import LaisOperationalPage from 'dashboard/components-next/LaisOperationalPage.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import LaisMetricStrip from 'dashboard/components-next/LaisMetricStrip.vue';
import LaisCardSkeleton from 'dashboard/components-next/LaisCardSkeleton.vue';
import LaisConfirmModal from 'dashboard/components-next/LaisConfirmModal.vue';
import SurveyFormCard from './components/SurveyFormCard.vue';
import SurveyFriendlyLinkModal from './components/SurveyFriendlyLinkModal.vue';
import {
  FIELD_INPUT_CLASS,
  FIELD_SELECT_CLASS,
  SURVEY_ROUTE_NAMES,
} from './constants';
import { useSurveyForms } from './composables/useSurveyForms';
import { useSurveyLabels } from './composables/useSurveyLabels';
import { FORM_TEMPLATES } from './formTemplates';

/**
 * Survey forms list.
 *
 * Owns discovery (search + kind/status filters), the aggregate strip and every
 * row action. Loading, empty, filtered-empty and error each have their own
 * state — the operator always knows why the screen is not showing surveys.
 */
const router = useRouter();
const { t, kindFilterOptions, activeFilterOptions } = useSurveyLabels();
const formTemplates = FORM_TEMPLATES;

const {
  filters,
  filteredForms,
  hasFilters,
  isFetching,
  hasLoaded,
  hasFetchError,
  fetchErrorMessage,
  isEmpty,
  isFilteredEmpty,
  summary,
  isPending,
  setFilter,
  resetFilters,
  fetchForms,
  duplicateForm,
  toggleActive,
  deleteForm,
} = useSurveyForms();

const formPendingDeletion = ref(null);
const isDeleting = ref(false);
const friendlyLinkForm = ref(null);

const pageError = computed(() => {
  if (!hasFetchError.value) return false;
  return fetchErrorMessage.value || t('SURVEYS.LIST.ERROR');
});

const showSkeleton = computed(() => isFetching.value && !hasLoaded.value);

const metricItems = computed(() => [
  {
    id: 'total',
    label: t('SURVEYS.LIST.METRICS.TOTAL'),
    value: summary.value.total,
    icon: 'i-lucide-clipboard-list',
  },
  {
    id: 'active',
    label: t('SURVEYS.LIST.METRICS.ACTIVE'),
    value: summary.value.active,
    tone: 'positive',
    icon: 'i-lucide-circle-check',
  },
  {
    id: 'sent',
    label: t('SURVEYS.LIST.METRICS.SENT'),
    value: summary.value.sent,
    icon: 'i-lucide-send',
  },
  {
    id: 'rate',
    label: t('SURVEYS.LIST.METRICS.RATE'),
    value: `${summary.value.rate}%`,
    helper: t('SURVEYS.LIST.METRICS.RATE_HELPER', {
      count: summary.value.submitted,
    }),
    tone: 'info',
    icon: 'i-lucide-percent',
  },
]);

const searchQuery = computed({
  get: () => filters.value.q,
  set: value => setFilter('q', value),
});

const kindFilter = computed({
  get: () => filters.value.kind,
  set: value => setFilter('kind', value),
});

const activeFilter = computed({
  get: () => filters.value.active,
  set: value => setFilter('active', value),
});

const goToNew = () => {
  router.push({ name: SURVEY_ROUTE_NAMES.new });
};

const goToTemplate = templateId => {
  router.push({
    name: SURVEY_ROUTE_NAMES.new,
    query: { template: templateId },
  });
};

const openFriendlyLink = form => {
  friendlyLinkForm.value = form;
};

const closeFriendlyLink = () => {
  friendlyLinkForm.value = null;
};

const onDuplicate = async form => {
  const result = await duplicateForm(form);
  useAlert(
    result.ok
      ? t('SURVEYS.LIST.ALERTS.VERSION_CREATED')
      : result.message || t('SURVEYS.LIST.ALERTS.VERSION_ERROR')
  );
};

const onToggle = async form => {
  const result = await toggleActive(form);
  if (!result.ok) {
    useAlert(result.message || t('SURVEYS.LIST.ALERTS.TOGGLE_ERROR'));
    return;
  }
  useAlert(
    form.active
      ? t('SURVEYS.LIST.ALERTS.DEACTIVATED')
      : t('SURVEYS.LIST.ALERTS.ACTIVATED')
  );
};

const askDelete = form => {
  if (Number(form.responses_count) > 0) {
    useAlert(t('SURVEYS.LIST.DELETE.BLOCKED'));
    return;
  }
  formPendingDeletion.value = form;
};

const closeDelete = () => {
  formPendingDeletion.value = null;
};

const confirmDelete = async () => {
  const form = formPendingDeletion.value;
  if (!form) return;

  if (Number(form.responses_count) > 0) {
    closeDelete();
    useAlert(t('SURVEYS.LIST.DELETE.BLOCKED'));
    return;
  }

  isDeleting.value = true;
  const result = await deleteForm(form);
  isDeleting.value = false;
  closeDelete();

  useAlert(
    result.ok
      ? t('SURVEYS.LIST.ALERTS.DELETED')
      : result.message || t('SURVEYS.LIST.ALERTS.DELETE_ERROR')
  );
};

const busyActionFor = form => {
  if (isPending('duplicate', form.id)) return 'duplicate';
  if (isPending('toggle', form.id)) return 'toggle';
  if (isPending('delete', form.id)) return 'delete';
  return '';
};

onMounted(fetchForms);
</script>

<template>
  <LaisOperationalPage
    :title="t('SURVEYS.HEADER')"
    :helper="t('SURVEYS.DESCRIPTION')"
    :error="pageError"
    @retry="fetchForms"
  >
    <template #actions>
      <LaisPrimaryButton icon="i-lucide-plus" @click="goToNew">
        {{ t('SURVEYS.LIST.NEW') }}
      </LaisPrimaryButton>
    </template>

    <template #metrics>
      <LaisMetricStrip
        :items="metricItems"
        :loading="showSkeleton"
        :aria-label="t('SURVEYS.LIST.METRICS.ARIA_LABEL')"
      />
    </template>

    <section
      class="rounded-lais border border-lais-border bg-lais-bg-card p-4 sm:p-5"
      :aria-label="t('SURVEYS.CATALOG.TITLE')"
    >
      <div class="mb-4 flex flex-col gap-1">
        <h2
          class="font-redHatDisplay text-lais-heading-sm font-semibold text-lais-text"
        >
          {{ t('SURVEYS.CATALOG.TITLE') }}
        </h2>
        <p class="text-lais-body-sm text-lais-text-helper">
          {{ t('SURVEYS.CATALOG.DESCRIPTION') }}
        </p>
      </div>

      <div class="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        <article
          v-for="formTemplate in formTemplates"
          :key="formTemplate.id"
          class="flex min-w-0 flex-col rounded-lais-sm border border-lais-border bg-white p-3"
        >
          <div class="flex items-start gap-2.5">
            <span
              class="grid size-9 shrink-0 place-items-center rounded-lais-sm bg-lais-primary-50 text-lais-primary"
              :class="formTemplate.icon"
              aria-hidden="true"
            />
            <div class="min-w-0">
              <h3 class="text-lais-body-sm font-semibold text-lais-text">
                {{ formTemplate.name }}
              </h3>
              <p
                class="mt-0.5 text-[13px] leading-relaxed text-lais-text-helper"
              >
                {{ formTemplate.description }}
              </p>
            </div>
          </div>
          <button
            type="button"
            class="mt-3 inline-flex h-9 items-center justify-center gap-1.5 self-start rounded-lais-sm border border-lais-border px-3 text-[13px] font-semibold text-lais-link transition hover:border-lais-primary hover:text-lais-link-hover focus-visible:outline-none focus-visible:shadow-lais-focus"
            @click="goToTemplate(formTemplate.id)"
          >
            <span class="i-lucide-file-plus-2 size-3.5" aria-hidden="true" />
            {{ t('SURVEYS.CATALOG.USE_TEMPLATE') }}
          </button>
        </article>
      </div>
    </section>

    <template #filters>
      <div class="flex min-w-0 flex-col gap-3 sm:flex-row sm:items-end">
        <label class="flex min-w-0 flex-1 flex-col gap-1.5">
          <span
            class="text-lais-label-xs font-semibold uppercase text-lais-text-muted"
          >
            {{ t('SURVEYS.LIST.FILTERS.SEARCH') }}
          </span>
          <input
            v-model="searchQuery"
            type="search"
            :class="FIELD_INPUT_CLASS"
            :placeholder="t('SURVEYS.LIST.FILTERS.SEARCH_PLACEHOLDER')"
          />
        </label>

        <label class="flex min-w-0 flex-col gap-1.5 sm:w-48">
          <span
            class="text-lais-label-xs font-semibold uppercase text-lais-text-muted"
          >
            {{ t('SURVEYS.LIST.FILTERS.KIND') }}
          </span>
          <select v-model="kindFilter" :class="FIELD_SELECT_CLASS">
            <option
              v-for="option in kindFilterOptions"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
        </label>

        <label class="flex min-w-0 flex-col gap-1.5 sm:w-44">
          <span
            class="text-lais-label-xs font-semibold uppercase text-lais-text-muted"
          >
            {{ t('SURVEYS.LIST.FILTERS.STATUS') }}
          </span>
          <select v-model="activeFilter" :class="FIELD_SELECT_CLASS">
            <option
              v-for="option in activeFilterOptions"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
        </label>

        <button
          v-if="hasFilters"
          type="button"
          class="inline-flex h-10 items-center gap-1.5 rounded-lais px-3 text-[13px] font-semibold text-lais-link transition hover:text-lais-link-hover focus-visible:outline-none focus-visible:shadow-lais-focus"
          @click="resetFilters"
        >
          <span class="i-lucide-x size-3.5" aria-hidden="true" />
          {{ t('SURVEYS.LIST.FILTERS.CLEAR') }}
        </button>
      </div>
    </template>

    <LaisCardSkeleton v-if="showSkeleton" :count="3" />

    <div
      v-else-if="isEmpty"
      class="grid place-items-center gap-3 rounded-lais border border-dashed border-lais-border bg-lais-bg-card px-4 py-16 text-center"
    >
      <span
        class="i-lucide-clipboard-list size-10 text-lais-text-muted"
        aria-hidden="true"
      />
      <p class="text-lais-body-md font-semibold text-lais-text">
        {{ t('SURVEYS.LIST.EMPTY_TITLE') }}
      </p>
      <p class="max-w-md text-lais-body-sm text-lais-text-helper">
        {{ t('SURVEYS.LIST.EMPTY_BODY') }}
      </p>
      <LaisPrimaryButton icon="i-lucide-plus" @click="goToNew">
        {{ t('SURVEYS.LIST.EMPTY_CTA') }}
      </LaisPrimaryButton>
    </div>

    <div
      v-else-if="isFilteredEmpty"
      class="grid place-items-center gap-3 rounded-lais border border-dashed border-lais-border bg-lais-bg-card px-4 py-14 text-center"
    >
      <span
        class="i-lucide-search-x size-9 text-lais-text-muted"
        aria-hidden="true"
      />
      <p class="text-lais-body-md font-semibold text-lais-text">
        {{ t('SURVEYS.LIST.NO_RESULTS_TITLE') }}
      </p>
      <p class="max-w-md text-lais-body-sm text-lais-text-helper">
        {{ t('SURVEYS.LIST.NO_RESULTS_BODY') }}
      </p>
      <LaisPrimaryButton variant="ghost" @click="resetFilters">
        {{ t('SURVEYS.LIST.FILTERS.CLEAR') }}
      </LaisPrimaryButton>
    </div>

    <div v-else class="grid gap-4 xl:grid-cols-2">
      <SurveyFormCard
        v-for="form in filteredForms"
        :key="form.id"
        :form="form"
        :busy-action="busyActionFor(form)"
        @duplicate="onDuplicate(form)"
        @toggle="onToggle(form)"
        @delete="askDelete(form)"
        @friendly-link="openFriendlyLink(form)"
      />
    </div>

    <LaisConfirmModal
      :show="Boolean(formPendingDeletion)"
      :title="t('SURVEYS.LIST.DELETE.TITLE')"
      :description="
        t('SURVEYS.LIST.DELETE.DESCRIPTION', {
          name: formPendingDeletion ? formPendingDeletion.name : '',
        })
      "
      :confirm-text="t('SURVEYS.LIST.DELETE.CONFIRM')"
      :cancel-text="t('SURVEYS.LIST.DELETE.CANCEL')"
      :is-loading="isDeleting"
      icon="i-lucide-trash-2"
      icon-class="bg-lais-danger-50 text-lais-danger"
      @close="closeDelete"
      @confirm="confirmDelete"
    />

    <SurveyFriendlyLinkModal
      :show="Boolean(friendlyLinkForm)"
      :form="friendlyLinkForm"
      @close="closeFriendlyLink"
    />
  </LaisOperationalPage>
</template>
